import { useCallback, useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ScheduleIndex, ScheduleMonthDoc, ScheduleNowDoc, ScheduleSession } from '../types/types';
import { liveDemoOn } from './useLiveNow';

/* ── Module-level caches ─────────────────────────────────────────────────────
   One read per doc per page load, shared by every component, so repeat visits
   and StrictMode's double effects never re-fetch. A failed read is dropped from
   the cache so a later call can retry. */

let indexPromise: Promise<ScheduleIndex> | null = null;
let nowPromise: Promise<ScheduleNowDoc> | null = null;
const monthPromises: Record<string, Promise<ScheduleSession[]>> = {};

// meta/scheduleIndex — resolves an empty index if the doc doesn't exist yet
export const getScheduleIndex = (): Promise<ScheduleIndex> => {
  if (!indexPromise) {
    indexPromise = getDoc(doc(db, 'meta', 'scheduleIndex'))
      .then(snap => {
        const data = snap.exists() ? (snap.data() as Partial<ScheduleIndex>) : {};
        return { months: data.months || {}, lastSynced: data.lastSynced || '' };
      })
      .catch((err: Error) => { indexPromise = null; throw err; });
  }
  return indexPromise;
};

// meta/scheduleNow — resolves no sessions if the doc doesn't exist yet. Development builds
// with ?liveDemo=1 add the demo sessions of dev/liveDemo.
export const getScheduleNow = (): Promise<ScheduleNowDoc> => {
  if (!nowPromise) {
    nowPromise = getDoc(doc(db, 'meta', 'scheduleNow'))
      .then((snap): ScheduleNowDoc | Promise<ScheduleNowDoc> => {
        const data = snap.exists() ? (snap.data() as Partial<ScheduleNowDoc>) : {};
        const now = { sessions: data.sessions || [], from: data.from || '', to: data.to || '', lastSynced: data.lastSynced || '' };
        return process.env.NODE_ENV === 'development' && liveDemoOn() ? import('../dev/liveDemo').then(m => m.withDemoSessions(now)) : now;
      })
      .catch((err: Error) => { nowPromise = null; throw err; });
  }
  return nowPromise;
};

// scheduleMonths/{ym} sessions — resolves [] for a missing doc
export const getScheduleMonth = (ym: string): Promise<ScheduleSession[]> => {
  if (!Object.prototype.hasOwnProperty.call(monthPromises, ym)) {
    monthPromises[ym] = getDoc(doc(db, 'scheduleMonths', ym))
      .then(snap => (snap.exists() ? ((snap.data() as ScheduleMonthDoc).sessions || []) : []))
      .catch((err: Error) => { delete monthPromises[ym]; throw err; });
  }
  return monthPromises[ym];
};

const EMPTY_INDEX: ScheduleIndex = { months: {}, lastSynced: '' };

/* ── Hook ─────────────────────────────────────────────────────────────────── */

// Loads the schedule index plus `initialMonths` ('YYYY-MM'); more months are added
// on demand with ensureMonth(). `sessions` is every loaded month, sorted by date.
export const useScheduleMonths = (initialMonths: string[]) => {
  const [monthSessions, setMonthSessions] = useState<Record<string, ScheduleSession[]>>({});
  const [index, setIndex] = useState<ScheduleIndex | null>(null);
  const [loading, setLoading] = useState(true);

  // Loads a month if needed. Safe to call repeatedly; a failed month shows as empty.
  const ensureMonth = useCallback((ym: string): Promise<void> => {
    return getScheduleMonth(ym)
      .catch((): ScheduleSession[] => [])
      .then(list => {
        setMonthSessions(prev => (prev[ym] === list ? prev : { ...prev, [ym]: list }));
      });
  }, []);

  // Keyed by value so a new array literal from the caller doesn't re-run the load
  const initialKey = initialMonths.join(',');
  useEffect(() => {
    let cancelled = false;
    const months = initialKey ? initialKey.split(',') : [];
    Promise.all([
      getScheduleIndex().catch((): ScheduleIndex => EMPTY_INDEX),
      Promise.all(months.map(ym => getScheduleMonth(ym).catch((): ScheduleSession[] => []))),
    ]).then(([idx, lists]) => {
      if (cancelled) return;
      setIndex(idx);
      setMonthSessions(prev => {
        const next = { ...prev };
        for (let i = 0; i < months.length; i++) next[months[i]] = lists[i];
        return next;
      });
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [initialKey]);

  const loadedMonths = useMemo(() => new Set<string>(Object.keys(monthSessions)), [monthSessions]);

  const sessions = useMemo(() => {
    const keys = Object.keys(monthSessions).sort();
    let all: ScheduleSession[] = [];
    for (let i = 0; i < keys.length; i++) all = all.concat(monthSessions[keys[i]]);
    all.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    return all;
  }, [monthSessions]);

  return { sessions, loadedMonths, ensureMonth, index, loading, lastSynced: index ? index.lastSynced : '' };
};
